"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { FormField, TextInput } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { Modal } from "@/components/ui/Modal";
import { TermsContent } from "@/components/legal/TermsContent";
import { PrivacyContent } from "@/components/legal/PrivacyContent";
import { SnsAuthButtons } from "@/components/auth/SnsAuthButtons";
import { apiFetch, ApiError, errorMessage } from "@/lib/api-client";

export default function SignupPage() {
  const router = useRouter();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [openModal, setOpenModal] = useState<"terms" | "privacy" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function toggleRole(role: string) {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch("/api/auth/signup", {
        method: "POST",
        json: { displayName, email, password, roles, agreeTerms, agreePrivacy },
      });
      router.push("/dashboard");
      router.refresh();
    } catch (err) {
      setError(err instanceof ApiError ? describeError(err) : errorMessage(err, "회원가입에 실패했습니다."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <h1 className="font-display text-3xl text-ink">회원가입</h1>
      <p className="mt-2 text-sm text-body">ARTPASS 계정을 만들고 작품 등록을 시작하세요.</p>

      <Card variant="plain" className="mt-8">
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {error && <Alert tone="error">{error}</Alert>}
          <FormField label="이름" htmlFor="displayName" required>
            <TextInput id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </FormField>
          <FormField label="이메일" htmlFor="email" required>
            <TextInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </FormField>
          <FormField label="비밀번호" htmlFor="password" required hint="8자 이상">
            <TextInput
              id="password"
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormField>
          <FormField label="역할 (선택)" hint="작가 또는 화랑으로 활동하는 경우 선택하세요.">
            <div className="flex gap-4 text-sm text-body">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={roles.includes("ARTIST")} onChange={() => toggleRole("ARTIST")} />
                작가
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={roles.includes("GALLERY")} onChange={() => toggleRole("GALLERY")} />
                화랑
              </label>
            </div>
          </FormField>
          <div className="flex flex-col gap-2 text-sm text-body">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                required
              />
              <span>
                <button
                  type="button"
                  onClick={() => setOpenModal("terms")}
                  className="text-primary underline underline-offset-4 hover:no-underline"
                >
                  이용약관
                </button>
                에 동의합니다. <span className="text-primary">*</span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                required
              />
              <span>
                <button
                  type="button"
                  onClick={() => setOpenModal("privacy")}
                  className="text-primary underline underline-offset-4 hover:no-underline"
                >
                  개인정보처리방침
                </button>
                에 동의합니다. <span className="text-primary">*</span>
              </span>
            </label>
          </div>
          <Button type="submit" disabled={submitting || !agreeTerms || !agreePrivacy}>
            {submitting ? "처리중..." : "회원가입"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-soft">
          <span className="h-px flex-1 bg-hairline" />
          또는
          <span className="h-px flex-1 bg-hairline" />
        </div>
        <SnsAuthButtons />
      </Card>

      <Modal open={openModal === "terms"} onClose={() => setOpenModal(null)} title="이용약관">
        <TermsContent />
      </Modal>
      <Modal open={openModal === "privacy"} onClose={() => setOpenModal(null)} title="개인정보처리방침">
        <PrivacyContent />
      </Modal>

      <p className="mt-6 text-center text-sm text-muted">
        이미 계정이 있으신가요?{" "}
        <Link href="/login" className="text-primary hover:underline">
          로그인
        </Link>
      </p>
    </div>
  );
}

function describeError(err: ApiError): string {
  if (err.status === 409) return "이미 가입된 이메일입니다.";
  return err.message;
}
